import React, { useState } from 'react';
import { useBrand, useFeatureEnabled } from '@equilateral/ui-shared';
import { API_ENDPOINTS } from '../../services/api/endpoints';

interface PricingTier {
  name: string;
  price: string;
  period: string;
  features: string[];
  limitations?: string[];
  cta: string;
  highlighted?: boolean;
}

const PowerSpecLanding: React.FC = () => {
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const { isFeatureEnabled, primaryColor } = useBrand();

  const pricingTiers: PricingTier[] = [
    {
      name: 'Free',
      price: '$0',
      period: '/month',
      features: [
        '5 specifications',
        'Basic templates (User Stories, Bugs)',
        'Codebase scanning (10/month)',
        'Read-only dependency view',
        'Community support'
      ],
      limitations: [
        'No AI embellishment',
        'No curator workflow'
      ],
      cta: 'Get Started Free'
    },
    {
      name: 'Pro',
      price: '$29',
      period: '/user/month',
      features: [
        'Unlimited specifications',
        'All templates (5 Whys, FMEA, Custom)',
        'Codebase scanning (100/month)',
        'AI-powered embellishment',
        'Curator approval workflow',
        'Dependency matrix',
        'GitHub/GitLab integration',
        'Priority support'
      ],
      cta: 'Start 14-Day Trial',
      highlighted: true
    }
  ];

  const specTemplates = [
    {
      name: 'User Story',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      description: 'As a [persona], I want [goal], so that [benefit]'
    },
    {
      name: '5 Whys Analysis',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      description: 'Root cause analysis for bugs and issues'
    },
    {
      name: 'FMEA',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
      description: 'Failure mode and effects analysis for risk assessment'
    },
    {
      name: 'Technical Spec',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      description: 'Architecture decisions and implementation plans'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-violet-600 to-violet-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-block px-4 py-1 bg-violet-500/30 rounded-full text-sm font-medium mb-4">
              Spec-Driven Development
            </span>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Transform Ideas into Dev-Ready Specs
            </h1>
            <p className="text-xl md:text-2xl text-violet-100 mb-8 max-w-3xl mx-auto">
              AI-powered specification library for Product Managers and Technical Architects.
              Write once, sync with code, keep specs alive.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                className="bg-white text-violet-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-violet-50 transition-colors"
                onClick={() => setShowWaitlist(true)}
              >
                Start Free
              </button>
              <a
                href="#how-it-works"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/10 transition-colors"
              >
                See How It Works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Specs That Actually Stay Current
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Traditional specs rot the moment code ships. PowerSpec links your specifications
              to your codebase, alerting you when implementations drift.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Before PowerSpec</h3>
              <p className="text-gray-600">Specs live in Google Docs, Confluence, or Notion. Nobody updates them after launch.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">The Drift Problem</h3>
              <p className="text-gray-600">Code evolves but specs don't. New team members read outdated requirements.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">With PowerSpec</h3>
              <p className="text-gray-600">Specs link to code. When implementations change, your specs flag the drift.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Built for Product People */}
      <section className="py-16 bg-violet-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Built for Product Teams
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <h3 className="text-xl font-semibold text-violet-800 mb-4">Product Managers</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-violet-600 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Write user stories with AI-suggested acceptance criteria</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-violet-600 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Track which specs are implemented vs. pending</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-violet-600 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Curator workflow: approve specs before dev starts</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <h3 className="text-xl font-semibold text-violet-800 mb-4">Technical Architects</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-violet-600 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Scan codebases to discover undocumented features</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-violet-600 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Link specs to specific files, functions, or modules</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-violet-600 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Dependency matrix: see how specs relate to each other</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            How It Works
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-violet-700 font-bold text-xl">1</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Write Specs</h3>
              <p className="text-gray-600 text-sm">Use templates or start from scratch. AI suggests acceptance criteria and edge cases.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-violet-700 font-bold text-xl">2</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Link to Code</h3>
              <p className="text-gray-600 text-sm">Connect specs to your GitHub/GitLab repos. Tag specific files or directories.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-violet-700 font-bold text-xl">3</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Monitor Drift</h3>
              <p className="text-gray-600 text-sm">When linked code changes, PowerSpec flags specs for review.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-violet-700 font-bold text-xl">4</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Stay Current</h3>
              <p className="text-gray-600 text-sm">Accept drift, update specs, or investigate discrepancies. Specs stay alive.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Specification Templates */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Specification Templates
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {specTemplates.map((template) => (
              <div key={template.name} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center mb-4 text-violet-600">
                  {template.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{template.name}</h3>
                <p className="text-gray-600 text-sm">{template.description}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-500 mt-8">
            Plus custom templates for your team's specific needs
          </p>
        </div>
      </section>

      {/* AI Embellishment */}
      <section className="py-16 bg-violet-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-3 py-1 bg-violet-500/30 rounded-full text-sm font-medium mb-4">
                Pro Feature
              </span>
              <h2 className="text-3xl font-bold mb-4">
                AI-Powered Embellishment
              </h2>
              <p className="text-violet-100 mb-6">
                Write a rough draft. AI suggests acceptance criteria, identifies edge cases,
                and ensures your specs are complete before dev begins.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-violet-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Auto-generate acceptance criteria</span>
                </li>
                <li className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-violet-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Identify edge cases and failure modes</span>
                </li>
                <li className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-violet-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Suggest related specifications</span>
                </li>
              </ul>
            </div>
            <div className="bg-white/10 rounded-xl p-6 font-mono text-sm">
              <div className="text-violet-300 mb-2"># User Story (Draft)</div>
              <div className="text-white mb-4">As a user, I want to reset my password</div>
              <div className="text-violet-300 mb-2"># AI Suggestions</div>
              <div className="text-green-300 text-xs space-y-1">
                <div>+ Accept: Valid email receives reset link</div>
                <div>+ Accept: Link expires after 24 hours</div>
                <div>+ Accept: Old password no longer works</div>
                <div>+ Edge: Invalid email shows generic message</div>
                <div>+ Edge: Rate limit after 5 attempts</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
            Simple Pricing
          </h2>
          <p className="text-center text-gray-600 mb-12">
            Start free, upgrade when you need more
          </p>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {pricingTiers.map((tier) => (
              <div
                key={tier.name}
                className={`bg-white rounded-xl shadow-sm p-8 ${
                  tier.highlighted ? 'ring-2 ring-violet-600' : 'border border-gray-200'
                }`}
              >
                {tier.highlighted && (
                  <span className="bg-violet-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                )}
                <h3 className="text-2xl font-bold text-gray-900 mt-4">{tier.name}</h3>
                <div className="mt-4 mb-6">
                  <span className="text-4xl font-bold text-gray-900">{tier.price}</span>
                  <span className="text-gray-600">{tier.period}</span>
                </div>
                <ul className="space-y-3 mb-6">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-violet-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                {tier.limitations && (
                  <ul className="space-y-2 mb-6 pt-4 border-t border-gray-100">
                    {tier.limitations.map((limitation) => (
                      <li key={limitation} className="flex items-start gap-2 text-gray-500 text-sm">
                        <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <span>{limitation}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <button
                  className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                    tier.highlighted
                      ? 'bg-violet-600 text-white hover:bg-violet-700'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                  onClick={() => setShowWaitlist(true)}
                >
                  {tier.cta}
                </button>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-500 mt-8">
            Enterprise? <button className="text-violet-600 hover:text-violet-700 font-medium" onClick={() => setShowWaitlist(true)}>Contact us</button> for custom pricing
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-violet-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Stop Writing Specs That Rot
          </h2>
          <p className="text-violet-100 mb-8 max-w-2xl mx-auto">
            Join product teams who keep their specifications alive and in sync with code.
          </p>
          <button
            className="bg-white text-violet-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-violet-50 transition-colors"
            onClick={() => setShowWaitlist(true)}
          >
            Get Started Free
          </button>
        </div>
      </section>

      {/* Waitlist Modal */}
      {showWaitlist && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Join the Early Access List</h3>
            <p className="text-gray-600 mb-6">
              PowerSpec is in private beta. Join the waitlist to get early access.
            </p>
            <form className="space-y-4" onSubmit={async (e) => {
              e.preventDefault();
              if (!waitlistEmail || waitlistSubmitting) return;
              setWaitlistSubmitting(true);
              try {
                const response = await fetch(API_ENDPOINTS.BRANDS.WAITLIST, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    email: waitlistEmail,
                    brand: 'powerspec',
                  }),
                });
                const data = await response.json();
                if (data.success) {
                  setShowWaitlist(false);
                  setWaitlistEmail('');
                  alert('You have been added to the waitlist!');
                } else {
                  alert(data.message || 'Something went wrong. Please try again.');
                }
              } catch (err) {
                console.error('[PowerSpecLanding] Waitlist signup error:', err);
                alert('Something went wrong. Please try again.');
              } finally {
                setWaitlistSubmitting(false);
              }
            }}>
              <input
                type="email"
                placeholder="Work Email"
                required
                value={waitlistEmail}
                onChange={(e) => setWaitlistEmail(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-violet-600 focus:border-transparent"
              />
              <select className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-violet-600 focus:border-transparent">
                <option>Role</option>
                <option>Product Manager</option>
                <option>Technical Architect</option>
                <option>Engineering Manager</option>
                <option>Developer</option>
                <option>Other</option>
              </select>
              <div className="flex gap-4">
                <button
                  type="button"
                  className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  onClick={() => setShowWaitlist(false)}
                  disabled={waitlistSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700"
                  disabled={waitlistSubmitting}
                >
                  {waitlistSubmitting ? 'Joining...' : 'Join Waitlist'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PowerSpecLanding;
